// Tiny dependency-free i18n module.
//
// English is the canonical fallback for every missing key. The selected
// language persists in localStorage so reloads keep the user's choice.
//
// Tool names (`next_step`, `prev_step`, `interrupt`, `record_scanned_items`)
// MUST remain English everywhere — this catalog never translates them.

const STORAGE_KEY = "vta.language";
export const SUPPORTED_LANGUAGES = ["en", "de"];
export const DEFAULT_LANGUAGE = "en";

const LISTENERS = new Set();

const CATALOG = {
  en: {
    // Top-level controls
    "controls.workflow_label": "Workflow",
    "controls.start": "Start",
    "controls.stop": "Stop",
    "controls.add_video_source": "Add video source",
    "controls.capture_video": "Capture video",
    "controls.share_screen": "Share screen",
    "controls.insert_video": "Insert video file",
    "controls.reset": "Reset",
    "controls.select_area": "Select area",
    "controls.main_view": "Main view",
    "controls.capture_audio": "Capture audio",
    "controls.stop_audio": "Stop audio",
    "controls.unmute_mic": "Unmute mic",
    "controls.mute_mic": "Mute mic",
    "controls.silent_mode_on": "Silent mode: on",
    "controls.silent_mode_off": "Silent mode: off",
    "controls.silent_mode_hint": "Silent mode only mutes playback in this browser. The model still receives and sends audio.",
    "controls.prev": "Prev",
    "controls.next": "Next",
    "controls.language_label": "Language",
    // Packing proof panel
    "packing.title": "Packing proof",
    "packing.trainer_connected": "how.fm Trainer connected",
    "packing.trainer_missing": "not inside how.fm Trainer",
    "packing.order_none": "No order open in SwiftLOG",
    "packing.units": "{packed}/{total} units",
    "packing.check.filler": "Filler",
    "packing.check.cover": "Carton layer",
    "packing.check.beams": "Beams",
    "packing.state.ok": "OK",
    "packing.state.missing": "missing",
    "packing.state.unknown": "not checked",
    "packing.state.not_verified": "not verified",
    "packing.checking": "checking…",
    "packing.proofs": "Proof photos",
    "packing.proofs_none": "No photos for this order yet",
    "packing.stage.contents_filled": "Filled",
    "packing.stage.closed_fixed": "Covered + beams",
    "packing.stage.closing_check": "Closing check",
    "packing.stage.manual": "Manual photo",
    "packing.capture": "Take proof photo",
    "packing.saved": "saved",
    "packing.last_order": "Last order {id}: {count} photos",
    "status.proof_source_missing": "Proof camera (Source {n}) is not active.",
    // Status lines
    "status.disconnected": "disconnected.",
    "status.connecting": "connecting...",
    "status.connected": "connected. workflow control active.",
    "status.connected_simple": "connected.",
    "status.observing": "observing workflow...",
    "status.speaking": "model is speaking...",
    "status.finished_ready": "finished. ready to start again.",
    "status.finishing": "finished. stopping...",
    "status.idle": "idle",
    "status.audio_captured_connected": "audio captured and connected.",
    "status.audio_captured_idle": "audio captured. Connect when ready.",
    "status.audio_stopped_connected": "audio stopped. connected.",
    "status.audio_stopped_idle": "audio stopped.",
    "status.audio_stopped_video_ready": "audio stopped. video remains ready.",
    "status.requesting_mic": "requesting microphone permission...",
    "status.requesting_camera": "requesting camera permission...",
    "status.requesting_screen": "requesting screen sharing permission...",
    "status.starting_camera": "starting selected camera...",
    "status.camera_ready": "camera source ready.",
    "status.screen_ready": "screen source ready.",
    "status.video_file_ready": "local video source ready.",
    "status.screen_stopped": "screen sharing stopped.",
    "status.cam_permission_granted": "camera permission granted. Select a camera.",
    "status.media_stopped_text_chat": "media stopped. text chat remains connected.",
    "status.failed_to_connect": "failed to connect.",
    "status.error_prefix": "error: {msg}",
    "status.scan_place_doc": "Place the document in Source {n}. Scanning...",
    "status.scan_in_progress": "Scanning document...",
    "status.scan_source_2_required": "Source 2 is required for document scanning.",
    "status.scan_unreadable": "I can't read the document clearly.",
    "status.scan_error_generic": "Scan error.",
    "status.scan_error_with_message": "Scan error: {msg}",
    "status.hellmann_expert_source_1_required":
      "Source 1 workstation camera is required for Hellmann Expert.",
    "status.hellmann_expert_source_2_required":
      "Source 2 LFS screen share is required for Hellmann Expert.",
    "status.workflow_changed_hint":
      "Workflow selection changed. Disconnect and reconnect to start the selected workflow.",
    "status.lang_changed_hint":
      "Language changed. Disconnect and reconnect to use the new language with the model.",
    "status.media_is_ready": "{reason} media is ready.",
    // Steps
    "step.counter_unknown": "Step -",
    "step.no_active": "No active step",
    "step.connect_to_start": "Connect to start the workflow.",
    "step.next_eyebrow": "Next step",
    "step.next_with_indices": "Next · Step {idx} of {total}",
    "step.fallback_index": "Step {idx}",
    "step.waiting": "Waiting",
    "step.finish_label": "Finish",
    "step.finish_description": "Complete this step to finish the workflow.",
    "step.next_placeholder": "The next step will appear here.",
    "step.workflow_title_placeholder": "Workflow",
    // Auth
    "auth.signed_in_as": "Signed in as",
    "auth.sign_out": "Sign out",
    "auth.title": "Sign in",
    "auth.subtitle": "Enter your space name and code to continue.",
    "auth.space_label": "Space name",
    "auth.code_label": "Code / PIN",
    "auth.submit": "Sign in",
    "auth.missing_fields": "Enter your space name and code.",
    "auth.session_expired": "Your session has expired. Please sign in again.",
    "auth.signed_out": "signed out.",
    // Recorder
    "recorder.title": "Local recorder",
    "recorder.disclaimer":
      "Recorder is off by default. Recordings stay on this computer and are not uploaded. The recorder files are not sent anywhere.",
    "recorder.arm": "Arm workflow capture",
    "recorder.disarm": "Disarm",
    "recorder.start_free": "Start free capture",
    "recorder.stop_free": "Stop free capture",
    "recorder.recording_workflow": "Recording…",
    "recorder.free_disabled_while_workflow": "Free capture disabled",
    "recorder.workflow_disabled_while_free": "Workflow capture disabled",
    "recorder.finalizing": "Finalizing…",
    "recorder.badge.off": "Off",
    "recorder.badge.armed": "Armed",
    "recorder.badge.recording_workflow": "Recording (workflow)",
    "recorder.badge.recording_free": "Recording (free)",
    "recorder.badge.finalizing": "Finalizing",
    "recorder.badge.error": "Error",
    // Debug panel
    "debug.title": "Debug",
    "debug.subtitle": "Chat, settings, and live request/response metadata",
    "debug.fold": "Fold",
    "debug.clear": "Clear",
    "debug.state": "State",
    "debug.ws": "WS",
    "debug.audio_in": "Audio in",
    "debug.images": "Images",
    "debug.audio_out": "Audio out",
    "debug.errors": "Errors",
    "debug.voice": "Voice",
    "debug.thinking": "Thinking",
    "debug.thoughts": "Thoughts",
    "debug.log": "Log",
    "debug.clean": "clean",
    "debug.full_json": "full JSON",
    "debug.media": "media",
    "debug.autoscroll": "auto-scroll",
    "debug.chat_locked_placeholder":
      "Chat is read-only. Workflow checks are sent automatically.",
    "debug.chat_send_locked": "Locked",
    "debug.current_view": "Current view → model",
    "debug.composite_view": "Composite video → model",
    "debug.view_waiting": "No frames sent to the model yet.",
    // Area selection (per-source crop)
    "area.title": "Select the area sent to the model",
    "area.hint": "Drag a rectangle over the preview. Only this region is sent to the model.",
    "area.apply": "Apply area",
    "area.reset": "Use full frame",
    "area.cancel": "Cancel",
    "area.badge": "area",
    "area.only_when_idle": "Stop the workflow to change the selected area.",
    "status.area_selected": "Area selected for Source {n}.",
    "status.area_cleared": "Source {n} now uses the full frame.",
    // AI-assisted image processing notice (EU AI Act Art. 50 transparency).
    "ai_notice.title": "AI-assisted image processing",
    "ai_notice.body":
      "This app uses AI-assisted image processing (Gemini Live) to guide you through the workflow shown on screen. No performance-related data is collected. No biometric data is collected.",
    "ai_notice.ack": "Continue",
    // HTML language code (matches <html lang="...">).
    "html.lang": "en",
  },
  de: {
    "controls.workflow_label": "Workflow",
    "controls.start": "Start",
    "controls.stop": "Stopp",
    "controls.add_video_source": "Videoquelle hinzufügen",
    "controls.capture_video": "Video aufnehmen",
    "controls.share_screen": "Bildschirm teilen",
    "controls.insert_video": "Videodatei einfügen",
    "controls.reset": "Zurücksetzen",
    "controls.select_area": "Bereich auswählen",
    "controls.main_view": "Hauptansicht",
    "controls.capture_audio": "Audio aufnehmen",
    "controls.stop_audio": "Audio stoppen",
    "controls.unmute_mic": "Mikrofon einschalten",
    "controls.mute_mic": "Mikrofon stummschalten",
    "controls.silent_mode_on": "Stumm-Modus: an",
    "controls.silent_mode_off": "Stumm-Modus: aus",
    "controls.silent_mode_hint": "Der Stumm-Modus schaltet nur die Wiedergabe in diesem Browser stumm. Das Modell empfängt und sendet weiterhin Audio.",
    "controls.prev": "Zurück",
    "controls.next": "Weiter",
    "controls.language_label": "Sprache",
    "status.disconnected": "getrennt.",
    "status.connecting": "verbinde...",
    "status.connected": "verbunden. Workflow-Steuerung aktiv.",
    "status.connected_simple": "verbunden.",
    "status.observing": "beobachte Workflow...",
    "status.speaking": "Modell spricht...",
    "status.finished_ready": "abgeschlossen. bereit, neu zu starten.",
    "status.finishing": "abgeschlossen. wird beendet...",
    "status.idle": "Leerlauf",
    "status.audio_captured_connected": "Audio aufgenommen und verbunden.",
    "status.audio_captured_idle": "Audio aufgenommen. Verbinde, wenn bereit.",
    "status.audio_stopped_connected": "Audio gestoppt. verbunden.",
    "status.audio_stopped_idle": "Audio gestoppt.",
    "status.audio_stopped_video_ready": "Audio gestoppt. Video bleibt bereit.",
    "status.requesting_mic": "fordere Mikrofonberechtigung an...",
    "status.requesting_camera": "fordere Kameraberechtigung an...",
    "status.requesting_screen": "fordere Bildschirmfreigabe-Berechtigung an...",
    "status.starting_camera": "starte ausgewählte Kamera...",
    "status.camera_ready": "Kameraquelle bereit.",
    "status.screen_ready": "Bildschirmquelle bereit.",
    "status.video_file_ready": "Lokale Videoquelle bereit.",
    "status.screen_stopped": "Bildschirmfreigabe beendet.",
    "status.cam_permission_granted": "Kameraberechtigung erteilt. Wähle eine Kamera aus.",
    "status.media_stopped_text_chat": "Medien gestoppt. Text-Chat bleibt verbunden.",
    "status.failed_to_connect": "Verbindung fehlgeschlagen.",
    "status.error_prefix": "Fehler: {msg}",
    "status.scan_place_doc": "Halte das Dokument in Quelle {n}. Scannt...",
    "status.scan_in_progress": "Dokument wird gescannt...",
    "status.scan_source_2_required": "Quelle 2 wird für das Dokumenten-Scannen benötigt.",
    "status.scan_unreadable": "Ich kann das Dokument nicht klar lesen.",
    "status.scan_error_generic": "Scan-Fehler.",
    "status.scan_error_with_message": "Scan-Fehler: {msg}",
    "status.hellmann_expert_source_1_required":
      "Quelle 1 Arbeitsplatzkamera wird für Hellmann Expert benötigt.",
    "status.hellmann_expert_source_2_required":
      "Quelle 2 LFS-Bildschirmfreigabe wird für Hellmann Expert benötigt.",
    "status.workflow_changed_hint":
      "Workflow-Auswahl geändert. Trenne und verbinde neu, um den ausgewählten Workflow zu starten.",
    "status.lang_changed_hint":
      "Sprache geändert. Trenne und verbinde neu, damit das Modell die neue Sprache verwendet.",
    "status.media_is_ready": "{reason} Medien sind bereit.",
    "step.counter_unknown": "Schritt -",
    "step.no_active": "Kein aktiver Schritt",
    "step.connect_to_start": "Verbinden, um den Workflow zu starten.",
    "step.next_eyebrow": "Nächster Schritt",
    "step.next_with_indices": "Nächster · Schritt {idx} von {total}",
    "step.fallback_index": "Schritt {idx}",
    "step.waiting": "Warten",
    "step.finish_label": "Fertig",
    "step.finish_description": "Schließe diesen Schritt ab, um den Workflow zu beenden.",
    "step.next_placeholder": "Der nächste Schritt erscheint hier.",
    "step.workflow_title_placeholder": "Workflow",
    "auth.signed_in_as": "Angemeldet als",
    "auth.sign_out": "Abmelden",
    "auth.title": "Anmelden",
    "auth.subtitle": "Gib deinen Bereichsnamen und Code ein, um fortzufahren.",
    "auth.space_label": "Bereichsname",
    "auth.code_label": "Code / PIN",
    "auth.submit": "Anmelden",
    "auth.missing_fields": "Gib deinen Bereichsnamen und Code ein.",
    "auth.session_expired": "Deine Sitzung ist abgelaufen. Bitte erneut anmelden.",
    "auth.signed_out": "abgemeldet.",
    "recorder.title": "Lokaler Rekorder",
    "recorder.disclaimer":
      "Der Rekorder ist standardmäßig aus. Aufnahmen verbleiben auf diesem Computer und werden nicht hochgeladen. Die Rekorderdateien werden nirgendwohin gesendet.",
    "recorder.arm": "Workflow-Aufnahme aktivieren",
    "recorder.disarm": "Deaktivieren",
    "recorder.start_free": "Freie Aufnahme starten",
    "recorder.stop_free": "Freie Aufnahme stoppen",
    "recorder.recording_workflow": "Nimmt auf…",
    "recorder.free_disabled_while_workflow": "Freie Aufnahme deaktiviert",
    "recorder.workflow_disabled_while_free": "Workflow-Aufnahme deaktiviert",
    "recorder.finalizing": "Wird abgeschlossen…",
    "recorder.badge.off": "Aus",
    "recorder.badge.armed": "Aktiviert",
    "recorder.badge.recording_workflow": "Aufnahme (Workflow)",
    "recorder.badge.recording_free": "Aufnahme (frei)",
    "recorder.badge.finalizing": "Wird abgeschlossen",
    "recorder.badge.error": "Fehler",
    "debug.title": "Debug",
    "debug.subtitle": "Chat, Einstellungen und Live-Metadaten zu Anfrage/Antwort",
    "debug.fold": "Einklappen",
    "debug.clear": "Löschen",
    "debug.state": "Zustand",
    "debug.ws": "WS",
    "debug.audio_in": "Audio rein",
    "debug.images": "Bilder",
    "debug.audio_out": "Audio raus",
    "debug.errors": "Fehler",
    "debug.voice": "Stimme",
    "debug.thinking": "Denken",
    "debug.thoughts": "Gedanken",
    "debug.log": "Log",
    "debug.clean": "kompakt",
    "debug.full_json": "volles JSON",
    "debug.media": "Medien",
    "debug.autoscroll": "Auto-Scroll",
    "debug.chat_locked_placeholder":
      "Chat ist schreibgeschützt. Workflow-Prüfungen werden automatisch gesendet.",
    "debug.chat_send_locked": "Gesperrt",
    "debug.current_view": "Aktuelle Ansicht → Modell",
    "debug.composite_view": "Composite-Video → Modell",
    "debug.view_waiting": "Noch keine Frames an das Modell gesendet.",
    "area.title": "Wähle den an das Modell gesendeten Bereich",
    "area.hint": "Ziehe ein Rechteck über die Vorschau. Nur dieser Bereich wird an das Modell gesendet.",
    "area.apply": "Bereich übernehmen",
    "area.reset": "Vollbild verwenden",
    "area.cancel": "Abbrechen",
    "area.badge": "Bereich",
    "area.only_when_idle": "Stoppe den Workflow, um den ausgewählten Bereich zu ändern.",
    "status.area_selected": "Bereich für Quelle {n} ausgewählt.",
    "status.area_cleared": "Quelle {n} verwendet jetzt das Vollbild.",
    "ai_notice.title": "KI-gestützte Bildverarbeitung",
    "ai_notice.body":
      "Diese App verwendet KI-gestützte Bildverarbeitung (Gemini Live), um dich durch den auf dem Bildschirm angezeigten Workflow zu führen. Es werden keine leistungsbezogenen Daten erfasst. Es werden keine biometrischen Daten erfasst.",
    "ai_notice.ack": "Weiter",
    "packing.title": "Verpackungsnachweis",
    "packing.trainer_connected": "how.fm Trainer verbunden",
    "packing.trainer_missing": "nicht im how.fm Trainer",
    "packing.order_none": "Kein Auftrag in SwiftLOG geöffnet",
    "packing.units": "{packed}/{total} Einheiten",
    "packing.check.filler": "Füllmaterial",
    "packing.check.cover": "Kartonlage",
    "packing.check.beams": "Balken",
    "packing.state.ok": "OK",
    "packing.state.missing": "fehlt",
    "packing.state.unknown": "ungeprüft",
    "packing.state.not_verified": "nicht geprüft",
    "packing.checking": "prüfe…",
    "packing.proofs": "Nachweisfotos",
    "packing.proofs_none": "Noch keine Fotos zu diesem Auftrag",
    "packing.stage.contents_filled": "Gefüllt",
    "packing.stage.closed_fixed": "Abgedeckt + Balken",
    "packing.stage.closing_check": "Abschlussprüfung",
    "packing.stage.manual": "Manuelles Foto",
    "packing.capture": "Nachweisfoto aufnehmen",
    "packing.saved": "gespeichert",
    "packing.last_order": "Letzter Auftrag {id}: {count} Fotos",
    "status.proof_source_missing": "Nachweiskamera (Quelle {n}) ist nicht aktiv.",
    "html.lang": "de",
  },
};

function normalizeLanguage(value) {
  if (!value) return DEFAULT_LANGUAGE;
  const token = String(value).trim().toLowerCase().split("-", 1)[0];
  return SUPPORTED_LANGUAGES.includes(token) ? token : DEFAULT_LANGUAGE;
}

let current = (() => {
  try {
    return normalizeLanguage(localStorage.getItem(STORAGE_KEY));
  } catch (_) {
    return DEFAULT_LANGUAGE;
  }
})();

function applyHtmlLang(lang) {
  try {
    const code = CATALOG[lang]?.["html.lang"] || lang;
    if (document?.documentElement) {
      document.documentElement.setAttribute("lang", code);
    }
  } catch (_) {
    /* SSR or test envs without a real document — ignore */
  }
}

applyHtmlLang(current);

export function getLanguage() {
  return current;
}

export function setLanguage(next) {
  const normalized = normalizeLanguage(next);
  if (normalized === current) return current;
  current = normalized;
  try {
    localStorage.setItem(STORAGE_KEY, normalized);
  } catch (_) {
    /* private mode etc. — selection survives at least for this tab */
  }
  applyHtmlLang(current);
  for (const fn of LISTENERS) {
    try {
      fn(current);
    } catch (_) {
      /* one bad listener must not break the others */
    }
  }
  return current;
}

export function onLanguageChange(fn) {
  if (typeof fn !== "function") return () => {};
  LISTENERS.add(fn);
  return () => LISTENERS.delete(fn);
}

function format(template, vars) {
  if (!vars) return template;
  return String(template).replace(/\{(\w+)\}/g, (m, k) =>
    Object.prototype.hasOwnProperty.call(vars, k) ? String(vars[k]) : m,
  );
}

export function t(key, vars) {
  const dict = CATALOG[current] || CATALOG[DEFAULT_LANGUAGE];
  const en = CATALOG[DEFAULT_LANGUAGE][key];
  const value = dict[key] ?? en ?? key;
  return format(value, vars);
}

// Expose the catalog for tests; do not modify in place.
export const _catalogForTests = CATALOG;
